import { api } from './api';

export enum ReactionType {
  LIKE = 'LIKE',
  LOVE = 'LOVE',
  HELPFUL = 'HELPFUL',
}

export interface ToggleReactionResponse {
  action: 'added' | 'removed';
  message: string;
}

export interface ReactionStats {
  type: ReactionType;
  count: number;
}

export interface ReactionCountResponse {
  count: number;
}

export interface MyReaction {
  id: string;
  lessonId?: string | null;
  commentId?: string | null;
  type: ReactionType;
}

export interface RatingSummary {
  average: number | null;
  count: number;
  distribution: Record<string, number>;
}

export interface LessonRating extends RatingSummary {
  lessonId: string;
  myRating: number | null;
}

export interface RatingAnalyticsRow extends RatingSummary {
  id: string;
  title: string;
}

export interface RatingAnalytics {
  summary: RatingSummary;
  lessons: RatingAnalyticsRow[];
  modules: RatingAnalyticsRow[];
  courses: RatingAnalyticsRow[];
  bestLessons: RatingAnalyticsRow[];
  worstLessons: RatingAnalyticsRow[];
}

export interface RatingAnalyticsFilters {
  courseId?: string;
  moduleId?: string;
  from?: string;
  to?: string;
}

export const reactionsService = {
  async getLessonRating(lessonId: string): Promise<LessonRating> {
    const { data } = await api.get(`/reactions/lessons/${lessonId}/rating`);
    return data.data || data;
  },

  async rateLesson(lessonId: string, rating: number): Promise<LessonRating> {
    const { data } = await api.put(`/reactions/lessons/${lessonId}/rating`, { rating });
    return data.data || data;
  },

  async getRatingAnalytics(filters: RatingAnalyticsFilters = {}): Promise<RatingAnalytics> {
    const { data } = await api.get('/reactions/ratings/analytics', { params: filters });
    return data.data || data;
  },
  async toggleLessonReaction(
    lessonId: string,
    type: ReactionType = ReactionType.LIKE,
  ): Promise<ToggleReactionResponse> {
    const { data } = await api.post(`/reactions/lessons/${lessonId}`, { type });
    return data?.data || data;
  },

  async toggleCommentReaction(
    commentId: string,
    type: ReactionType = ReactionType.LIKE,
  ): Promise<ToggleReactionResponse> {
    const { data } = await api.post(`/reactions/comments/${commentId}`, {
      type,
    });
    return data?.data || data;
  },

  async getLessonStats(lessonId: string): Promise<ReactionStats[]> {
    const { data } = await api.get(`/reactions/lessons/${lessonId}/stats`);
    return data?.data || data;
  },

  async getCommentReactionCount(
    commentId: string,
  ): Promise<ReactionCountResponse> {
    const { data } = await api.get(`/reactions/comments/${commentId}/count`);
    return data?.data || data;
  },

  async getMyReactions(lessonId?: string): Promise<MyReaction[]> {
    const { data } = await api.get('/reactions/me', {
      params: lessonId ? { lessonId } : {},
    });
    return data?.data || data;
  },
};
